import type { Cron } from "@storytell/client";
import type { Component } from "solid-js";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { ActionDetail, ActionsList, WebSidebar } from "./internal/actions";
import { MockScene, type Person } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

type ActionRun = { title: string; hoursAgo: number; credits: number };

/** Times count back from `now`, an ISO time read as UTC, so the scene renders the same anywhere. */
export type ActionsData = {
  url: string;
  project: string;
  user: Person;
  now: string;
  /** The sidebar's recent tasks, all from today, newest first. */
  tasks: { title: string; hoursAgo: number }[];
  /**
   * Newest first, as the list sorts by default; the first is open in the side
   * panel. An action runs on `cron` or on a `trigger` kind, as the app saves it.
   */
  actions: {
    name: string;
    description: string;
    hoursAgo: number;
    cron?: Cron;
    trigger?: string;
    paused?: boolean;
    slack?: string;
    runs: ActionRun[];
  }[];
  prompt: string;
  recipients: string[];
  /** The run Run Now starts. */
  ran: ActionRun;
};

/**
 * Actions in the web app, played once: the project's actions in their grid, one
 * open in the side panel, where Run Now starts a run that lands in its history,
 * finishes, and shows the file it produced.
 */
export const ActionsMockup: Component<{ data: ActionsData; class?: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();

  return (
    <MockScene scene={scene} label={t("marketing.mock.actions.page_alt")} class={props.class}>
      <ScaledFrame
        chrome="browser"
        width={1120}
        height={700}
        narrow={{ width: 400, height: 880 }}
        title={props.data.url}
      >
        <div class="flex h-full bg-canvas">
          <div class="hidden @3xl:block">
            <WebSidebar data={props.data} />
          </div>
          <div class="hidden min-w-0 flex-1 @3xl:block">
            <ActionsList data={props.data} />
          </div>
          <div class="size-full shrink-0 @3xl:w-111 @3xl:p-0.5">
            <ActionDetail data={props.data} />
          </div>
        </div>
      </ScaledFrame>
    </MockScene>
  );
};
